import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  createMultiplePartitionThunk,
  deletePartitionThunk,
} from "store/reducers/vending";
import { LoadingButton } from "@mui/lab";

const validation = yup.object({
  max_count: yup
    .number("Enter how many slots are available in this tray")
    .required("Slot count required")
    .positive()
    .min(1),
  count: yup
    .number("Enter how many partitions needed")
    .required("Username required")
    .positive()
    .min(1),
});

const PartitionCreateWizardForm = ({
  handleNext,
  handleSkip,
  trayStepCompleted,
  partitionStepCompleted,
  setPartitionStepCompleted,
}) => {
  const isSmallSCreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const vendingState = useSelector((state) => state.vending);
  const dispatch = useDispatch();
  const [cabin, setCabin] = React.useState("");
  const [tray, setTray] = React.useState("");
  const handleCabinChange = (event) => {
    setCabin(event.target.value);
  };

  const handleTrayChange = (event) => {
    setTray(event.target.value);
  };

  const formik = useFormik({
    initialValues: {
      count: 1,
      max_count: 10,
    },
    validationSchema: validation,
    onSubmit: (data) => {
      data["tray"] = tray;
      console.log(data);
      dispatch(createMultiplePartitionThunk(data));
    },
  });

  useEffect(() => {
    if (vendingState.createMultiplePartitionSuccess[tray]) {
      setPartitionStepCompleted(true);
    }
  }, [
    setPartitionStepCompleted,
    tray,
    vendingState.createMultiplePartitionSuccess,
  ]);

  return (
    <Box sx={{ mb: 2 }}>
      {trayStepCompleted ? (
        <>
          <Typography variant="h6" color="gray">
            Please select a cabin and tray
          </Typography>
          <Box display="flex" marginTop={2} flexGrow={1}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="cabin">Cabin</InputLabel>
              <Select
                id="id-cabin-select"
                value={cabin}
                label="Cabin"
                onChange={handleCabinChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {vendingState.cabins[vendingState.currentMachine.id]?.map(
                  (cabinItem) => (
                    <MenuItem key={cabinItem.id} value={cabinItem.id}>
                      Cabin {cabinItem.number}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl sx={{ ml: 1, minWidth: 120 }} size="small">
              <InputLabel id="cabin">Tray</InputLabel>
              <Select
                id="id-tray-select"
                value={tray}
                label="Tray"
                onChange={handleTrayChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {vendingState.trays[cabin]?.map((tray) => (
                  <MenuItem key={tray.id} value={tray.id}>
                    Tray {tray.number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box marginTop={2}>
            {cabin && tray ? (
              <>
                {(vendingState.partitions[tray]?.length ?? 0) > 0 ? (
                  <Grid container columnSpacing={2} rowSpacing={0}>
                    <Grid item xs={12} md={6} lg={6}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {!isSmallSCreen && <TableCell>Name</TableCell>}
                              <TableCell align="center">Number</TableCell>
                              <TableCell align="center">Max count</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {vendingState.partitions[tray]?.map((partition) => (
                              <TableRow
                                key={partition.id}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                {!isSmallSCreen && (
                                  <TableCell component="th" scope="row">
                                    {`Partition ${partition.number}`}
                                  </TableCell>
                                )}
                                <TableCell align="center">
                                  {partition.number}
                                </TableCell>
                                <TableCell align="center">
                                  {partition.max_count}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    color="error"
                                    onClick={() =>
                                      dispatch(deletePartitionThunk(partition))
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="h6" color="gray">
                    No partitions in this tray . Add a new partition
                  </Typography>
                )}
                <Box marginTop={isSmallSCreen ? 2 : 3}>
                  <form onSubmit={formik.handleSubmit}>
                    <Grid container columnSpacing={2} rowSpacing={0}>
                      <Grid item xs={12} md={6} lg={6}>
                        <Box
                          display="flex"
                          flexDirection={isSmallSCreen ? "column" : "row"}
                          justifyItems="center"
                          alignItems={isSmallSCreen ? "start" : "center"}
                        >
                          <TextField
                            label="Enter slot count"
                            variant="outlined"
                            name="max_count"
                            type="number"
                            fullWidth
                            sx={{
                              mt: { lg: 0, md: 2, xs: 2 },
                            }}
                            value={formik.values.max_count}
                            onChange={formik.handleChange}
                            error={Boolean(formik.errors.max_count)}
                            helperText={formik.errors.max_count}
                            autoComplete="new-password"
                            required
                          />
                          <TextField
                            label="How many items required"
                            variant="outlined"
                            name="count"
                            type="number"
                            fullWidth
                            sx={{
                              ml: { lg: 1, md: 0 },
                              mt: { lg: 0, md: 2, xs: 2 },
                            }}
                            value={formik.values.count}
                            onChange={formik.handleChange}
                            error={Boolean(formik.errors.count)}
                            helperText={formik.errors.count}
                            autoComplete="new-password"
                            required
                          />
                          <LoadingButton
                            loading={
                              vendingState.createMultiplePartitionLoading[
                                tray
                              ] ?? false
                            }
                            variant="outlined"
                            type="submit"
                            sx={{
                              ml: { lg: 1, md: 0 },
                              mt: { lg: 0, md: 2, xs: 2 },
                            }}
                          >
                            Add
                          </LoadingButton>
                        </Box>
                        {(vendingState.createMultiplePartitionError[tray] ??
                          false) && (
                          <Alert sx={{ mt: 3 }} severity="error">
                            Error occurred
                          </Alert>
                        )}
                        {vendingState.createMultiplePartitionSuccess[tray] && (
                          <Alert sx={{ mt: 3 }} severity="success">
                            Success
                          </Alert>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row-reverse",
                            alignItems: "center",
                            marginTop: 3,
                          }}
                        >
                          {partitionStepCompleted && (
                            <Button
                              variant="contained"
                              fullWidth={isSmallSCreen}
                              type="submit"
                              onClick={handleNext}
                            >
                              Continue
                            </Button>
                          )}
                          <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                          >
                            Skip
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </>
            ) : (
              <Grid container columnSpacing={2} rowSpacing={0}>
                <Grid item xs={12} md={6} lg={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row-reverse",
                      alignItems: "center",
                      marginTop: 3,
                    }}
                  >
                    <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                      Skip
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </>
      ) : (
        <div>
          <Typography variant="h6" color="gray">
            This step can not be proceeded without trays
          </Typography>
          <Grid container columnSpacing={2} rowSpacing={0}>
            <Grid item xs={12} md={6} lg={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row-reverse",
                  alignItems: "center",
                  marginTop: 3,
                }}
              >
                <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                  Skip
                </Button>
              </Box>
            </Grid>
          </Grid>
        </div>
      )}
    </Box>
  );
};

export default PartitionCreateWizardForm;
